import React from "react";
import { Button } from "../../ui/button";
import { Loader2Icon, RocketIcon } from "lucide-react";
import moment from "moment";
import { S3Version } from "../workflowConstants";

interface WorkflowVersionsTabProps {
  versions: S3Version[];
  versionsLoading: boolean;
  deployingVersionId: string | null;
  onDeploy: (versionId: string) => void;
}

const WorkflowVersionsTab: React.FC<WorkflowVersionsTabProps> = ({
  versions,
  versionsLoading,
  deployingVersionId,
  onDeploy,
}) => {
  if (versionsLoading) {
    return (
      <div className="space-y-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="flex items-center justify-between p-4 rounded-lg border border-border">
            <div className="space-y-1.5">
              <div className="h-4 bg-muted rounded w-48 animate-pulse" />
              <div className="h-3 bg-muted rounded w-32 animate-pulse" />
            </div>
            <div className="h-6 bg-muted rounded w-16 animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">
        No versions yet. Save the workflow script to create the first version.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {versions.map((v, idx) => (
        <div
          key={v.version_id}
          className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-muted/30 transition-colors"
        >
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-gc-med text-foreground">
                Version {versions.length - idx}
              </span>
              {v.is_deployed && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-maitai-green/10 text-maitai-green font-gc-med uppercase">
                  Deployed
                </span>
              )}
              {v.is_latest && !v.is_deployed && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-gc-med uppercase">
                  Latest
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {v.last_modified
                ? moment(v.last_modified).format("MMM D, YYYY h:mm A")
                : "—"}
              {v.size != null && (
                <span className="ml-2">
                  ({(v.size / 1024).toFixed(1)} KB)
                </span>
              )}
            </div>
          </div>
          {!v.is_deployed && (
            <Button
              variant="ghost"
              size="sm"
              className="gap-1 text-xs"
              disabled={deployingVersionId === v.version_id}
              onClick={() => onDeploy(v.version_id)}
            >
              {deployingVersionId === v.version_id ? (
                <Loader2Icon className="w-3 h-3 animate-spin" />
              ) : (
                <RocketIcon className="w-3 h-3" />
              )}
              Deploy
            </Button>
          )}
        </div>
      ))}
    </div>
  );
};

export default WorkflowVersionsTab;
